import { forwardRef } from 'react'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { Alert, AlertDescription } from '../ui/alert'
import { cn } from '../../lib/utils'
import { AlertTriangle, Info } from 'lucide-react'

interface BaseValidatedFieldProps {
  label?: string
  error?: string
  warning?: string
  required?: boolean
  className?: string
}

interface ValidatedInputProps extends BaseValidatedFieldProps, React.InputHTMLAttributes<HTMLInputElement> {}

interface ValidatedTextareaProps extends BaseValidatedFieldProps, React.TextareaHTMLAttributes<HTMLTextAreaElement> {}

export const ValidatedInput = forwardRef<HTMLInputElement, ValidatedInputProps>(
  ({ label, error, warning, required, className, ...props }, ref) => {
    const hasError = !!error
    const hasWarning = !!warning && !hasError

    return (
      <div className="space-y-2">
        {label && (
          <Label 
            htmlFor={props.id} 
            className={cn(
              required && 'after:content-["*"] after:text-red-500 after:ml-1'
            )}
          >
            {label}
          </Label>
        )}
        <Input
          ref={ref}
          className={cn(
            hasError && 'border-red-500 focus:border-red-500 focus:ring-red-500',
            hasWarning && 'border-yellow-500 focus:border-yellow-500 focus:ring-yellow-500',
            className
          )}
          aria-invalid={hasError}
          aria-describedby={
            hasError 
              ? `${props.id}-error` 
              : hasWarning 
                ? `${props.id}-warning` 
                : undefined
          }
          {...props}
        />
        {hasError && (
          <Alert variant="destructive" className="py-2">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription 
              id={`${props.id}-error`}
              className="text-sm"
            >
              {error}
            </AlertDescription>
          </Alert>
        )}
        {hasWarning && !hasError && (
          <Alert className="py-2 border-yellow-500 bg-yellow-50 dark:bg-yellow-950/20">
            <Info className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
            <AlertDescription 
              id={`${props.id}-warning`}
              className="text-sm text-yellow-700 dark:text-yellow-300"
            >
              {warning}
            </AlertDescription>
          </Alert>
        )}
      </div>
    )
  }
)

ValidatedInput.displayName = 'ValidatedInput'

export const ValidatedTextarea = forwardRef<HTMLTextAreaElement, ValidatedTextareaProps>(
  ({ label, error, warning, required, className, ...props }, ref) => {
    const hasError = !!error
    const hasWarning = !!warning && !hasError

    return (
      <div className="space-y-2">
        {label && (
          <Label 
            htmlFor={props.id} 
            className={cn(
              required && 'after:content-["*"] after:text-red-500 after:ml-1'
            )}
          >
            {label}
          </Label>
        )}
        <Textarea
          ref={ref}
          className={cn(
            hasError && 'border-red-500 focus:border-red-500 focus:ring-red-500',
            hasWarning && 'border-yellow-500 focus:border-yellow-500 focus:ring-yellow-500',
            className
          )}
          aria-invalid={hasError}
          aria-describedby={
            hasError 
              ? `${props.id}-error` 
              : hasWarning 
                ? `${props.id}-warning` 
                : undefined
          }
          {...props}
        />
        {hasError && (
          <Alert variant="destructive" className="py-2">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription 
              id={`${props.id}-error`}
              className="text-sm"
            >
              {error}
            </AlertDescription>
          </Alert>
        )}
        {hasWarning && !hasError && (
          <Alert className="py-2 border-yellow-500 bg-yellow-50 dark:bg-yellow-950/20">
            <Info className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
            <AlertDescription 
              id={`${props.id}-warning`}
              className="text-sm text-yellow-700 dark:text-yellow-300"
            >
              {warning}
            </AlertDescription>
          </Alert>
        )}
      </div>
    )
  }
)

ValidatedTextarea.displayName = 'ValidatedTextarea'

// Additional validated components for common form patterns

interface ValidatedNumberInputProps extends ValidatedInputProps {
  min?: number
  max?: number
  step?: number
}

export const ValidatedNumberInput = forwardRef<HTMLInputElement, ValidatedNumberInputProps>(
  ({ min, max, step = 'any', ...props }, ref) => {
    return (
      <ValidatedInput
        ref={ref}
        type="number"
        min={min}
        max={max}
        step={step}
        {...props}
      />
    )
  }
)

ValidatedNumberInput.displayName = 'ValidatedNumberInput'

// Helper component for displaying multiple validation errors
interface ValidationSummaryProps {
  errors: Record<string, string[]>
  className?: string
}

export function ValidationSummary({ errors, className }: ValidationSummaryProps) {
  const errorCount = Object.keys(errors).length
  
  if (errorCount === 0) return null

  return (
    <Alert variant="destructive" className={cn('mb-4', className)}>
      <AlertTriangle className="h-4 w-4" />
      <AlertDescription>
        <div className="space-y-1">
          <p className="font-medium">
            Please fix the following {errorCount === 1 ? 'error' : 'errors'}:
          </p>
          <ul className="list-disc list-inside space-y-1 text-sm">
            {Object.entries(errors).map(([field, fieldErrors]) => 
              fieldErrors.map((error, index) => (
                <li key={`${field}-${index}`}>
                  {field !== 'general' && <span className="font-medium">{field}:</span>} {error}
                </li>
              ))
            )}
          </ul>
        </div>
      </AlertDescription>
    </Alert>
  )
}

// Hook for managing form field validation state
export function useFieldValidation() {
  return {
    getFieldError: (errors: Record<string, string[]> | undefined, fieldName: string): string | undefined => {
      return errors?.[fieldName]?.[0]
    },
    hasFieldError: (errors: Record<string, string[]> | undefined, fieldName: string): boolean => {
      return !!(errors?.[fieldName]?.length)
    },
    getFieldProps: (errors: Record<string, string[]> | undefined, fieldName: string) => ({
      error: errors?.[fieldName]?.[0],
      'aria-invalid': !!(errors?.[fieldName]?.length),
      'aria-describedby': errors?.[fieldName]?.length ? `${fieldName}-error` : undefined
    })
  }
}